import type { ReactNode } from "react";
import { IconButton } from "@/components/ui/icon-button";
import { InfoIcon } from "@/components/ui/info-icon";
import { Pressable, RowButton } from "@/components/ui/pressable";
import { UserRound } from "lucide-react";
import { useMemo, useState } from "react";
import { useNotify, useUpdate, type Identifier } from "ra-core";
import type { OrganizationMember, Ticket } from "@/modules/types";
import { toneClass } from "@/modules/shared/status";
import { useTicketStatusChoices } from "@/modules/settings/tickets/useTicketWorkspaceSettings";
import {
  ticketStatusLabel,
  ticketStatusTone,
} from "@/modules/tickets/ticketInboxConfig";
import { memberDisplayName } from "@/modules/tickets/ticketInboxUi";
import { useTicketStatusChange } from "@/modules/tickets/useTicketStatusChange";
import { SignedMemberAvatarImage } from "@/components/avatar/SignedMemberAvatarImage";
import { Avatar, AvatarFallback } from "@/components/ui/avatar";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/components/ui/popover";
import { cn } from "@/lib/utils";
import {
  getMemberInitials,
  getMemberName,
} from "@/components/atomic-crm/tasks/taskMemberOptions";

const STATUS_OPTIONS = [
  { id: "new" },
  { id: "open" },
  { id: "waiting" },
  { id: "resolved" },
] as const;

export const TicketListStatusControl = ({
  ticket,
  canManage,
  onUpdated,
  children,
}: {
  ticket: Ticket;
  canManage: boolean;
  onUpdated?: () => void;
  /** Custom trigger content (e.g. the header's status pill) instead of the dot. */
  children?: ReactNode;
}) => {
  const [menuOpen, setMenuOpen] = useState(false);
  const { applyStatusChange, statusChangeDialog } =
    useTicketStatusChange(onUpdated);
  const statusChoices = useTicketStatusChoices();
  const statusOptions = useMemo(() => {
    const builtin = new Set(["new", "open", "waiting", "resolved"]);
    const extras = statusChoices
      .map((choice) => choice.id)
      .filter((id) => !builtin.has(id));
    return [...STATUS_OPTIONS.map((option) => option.id), ...extras];
  }, [statusChoices]);

  const handleChange = (status: string) => {
    if (!canManage) return;
    setMenuOpen(false);
    applyStatusChange(ticket, status);
  };

  const statusLabel = `Status: ${ticketStatusLabel(ticket.status)}`;
  const statusDot = (
    <span
      className={cn(
        "size-2.5 rounded-full",
        toneClass(ticketStatusTone(ticket.status), "dot"),
      )}
      aria-hidden
    />
  );

  // Read-only: an indicator, not a button.
  if (!canManage) {
    if (children) return <>{children}</>;
    return (
      <InfoIcon shape="circle" label={statusLabel} ariaLabel={statusLabel}>
        {statusDot}
      </InfoIcon>
    );
  }

  const trigger = children ? (
    <Pressable
      className="inline-flex items-center rounded-md"
      aria-label={`${statusLabel}. Change status`}
      onClick={(event) => event.stopPropagation()}
    >
      {children}
    </Pressable>
  ) : (
    <IconButton
      variant="secondary"
      className="rounded-full"
      aria-label={statusLabel}
      onClick={(event) => event.stopPropagation()}
    >
      {statusDot}
    </IconButton>
  );

  return (
    <>
      {statusChangeDialog}
      <Popover open={menuOpen} onOpenChange={setMenuOpen}>
        <PopoverTrigger asChild>{trigger}</PopoverTrigger>
        <PopoverContent
          align="end"
          className="w-40 p-1"
          onClick={(event) => event.stopPropagation()}
        >
          {statusOptions.map((statusId) => (
            <RowButton
              layout="none"
              density="none"
              className={cn(
                "flex w-full items-center gap-2 rounded-md px-2 py-1.5 text-left text-sm hover:bg-accent",
                ticket.status === statusId && "bg-muted/60",
              )}
              key={statusId}
              onClick={() => handleChange(statusId)}
            >
              <span
                className={cn(
                  "size-2.5 rounded-full",
                  toneClass(ticketStatusTone(statusId), "dot"),
                )}
              />
              <span className="capitalize">{ticketStatusLabel(statusId)}</span>
            </RowButton>
          ))}
        </PopoverContent>
      </Popover>
    </>
  );
};

export const TicketListAssigneeControl = ({
  ticket,
  assignee,
  members,
  canManage,
  onUpdated,
}: {
  ticket: Ticket;
  assignee?: OrganizationMember | null;
  members: OrganizationMember[];
  canManage: boolean;
  onUpdated?: () => void;
}) => {
  const notify = useNotify();
  const [update] = useUpdate();

  const activeMembers = useMemo(
    () => members.filter((member) => member.disabled !== true),
    [members],
  );

  const handleChange = (assigneeId: Identifier | null) => {
    if (!canManage) return;
    update(
      "tickets",
      {
        id: ticket.id,
        data: { assignee_id: assigneeId },
        previousData: ticket,
      },
      {
        onSuccess: () => {
          notify("Ticket assigned", { type: "success" });
          onUpdated?.();
        },
        onError: () => notify("Failed to assign ticket", { type: "error" }),
      },
    );
  };

  const name = assignee ? getMemberName(assignee) : "Unassigned";

  const assigneeLabel = `Assignee: ${name}`;
  const assigneeAvatar = (
    <Avatar className="size-6">
      {assignee ? (
        <SignedMemberAvatarImage member={assignee} size={48} alt={name} />
      ) : null}
      <AvatarFallback className="text-2xs font-medium">
        {assignee ? (
          getMemberInitials(assignee)
        ) : (
          <UserRound className="size-3.5 text-muted-foreground" />
        )}
      </AvatarFallback>
    </Avatar>
  );

  // Read-only: an indicator, not a button.
  if (!canManage) {
    return (
      <InfoIcon shape="circle" label={assigneeLabel} ariaLabel={assigneeLabel}>
        {assigneeAvatar}
      </InfoIcon>
    );
  }

  const trigger = (
    <IconButton
      variant="secondary"
      className="rounded-full"
      aria-label={assigneeLabel}
      onClick={(event) => event.stopPropagation()}
    >
      {assigneeAvatar}
    </IconButton>
  );

  return (
    <Popover>
      <PopoverTrigger asChild>{trigger}</PopoverTrigger>
      <PopoverContent
        align="end"
        className="w-52 max-h-64 overflow-y-auto p-1"
        onClick={(event) => event.stopPropagation()}
      >
        <RowButton
          layout="none"
          density="none"
          className={cn(
            "flex w-full rounded-md px-2 py-1.5 text-left text-sm hover:bg-accent",
            !ticket.assignee_id && "bg-muted/60",
          )}
          onClick={() => handleChange(null)}
        >
          Unassigned
        </RowButton>
        {activeMembers.map((member) => (
          <RowButton
            layout="none"
            density="none"
            className={cn(
              "flex w-full items-center gap-2 rounded-md px-2 py-1.5 text-left text-sm hover:bg-accent",
              String(ticket.assignee_id) === String(member.id) && "bg-muted/60",
            )}
            key={String(member.id)}
            onClick={() => handleChange(member.id)}
          >
            <Avatar className="size-6">
              <SignedMemberAvatarImage
                member={member}
                size={48}
                alt={memberDisplayName(member) ?? "Member"}
              />
              <AvatarFallback className="text-2xs">
                {getMemberInitials(member)}
              </AvatarFallback>
            </Avatar>
            <span className="truncate">{memberDisplayName(member)}</span>
          </RowButton>
        ))}
      </PopoverContent>
    </Popover>
  );
};
